import { useEffect, useRef, useState } from 'hono/jsx';
import { ButtonSpinner, Spinner } from '../../components/Spinner';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import type { Site } from '../../lib/site';

type Role = 'logo' | 'brand_guide' | 'image' | 'copy' | 'other';
interface Material { file: string; role: Role; slot?: string; originalName: string; size: number; }

interface Brief {
  version: 1;
  business: { name: string; summary: string; location: string; audience: string; services: string; phone: string; address: string; hours: string };
  source: { url: string; use: 'content' | 'design' | 'both' | 'none' };
  brand: { logo: 'upload' | 'wordmark'; guide: 'upload' | 'colors' | 'none'; colors: string[]; fonts: string };
  images: { mode: 'upload' | 'mix' | 'generate' | 'none' };
  copy: { mode: 'upload' | 'paste' | 'write'; text: string };
  pages: string[];
  goal: string;
  cta: string;
  style: { aesthetic: string[]; references: string; avoid: string };
  notes: string;
}

interface Suggestion { pages: string[]; goal: string; needsDetail: boolean; hasLocation: boolean; via: string; }

const STEPS = ['Your business', 'Existing site', 'Brand', 'Photos', 'Words', 'Pages', 'Look and feel', 'Review'];

const PAGE_LABEL: Record<string, string> = {
  home: 'Home', about: 'About', services: 'Services', menu: 'Menu', pricing: 'Pricing', gallery: 'Gallery',
  team: 'Team', testimonials: 'Testimonials', faq: 'FAQ', blog: 'Blog', booking: 'Booking', contact: 'Contact',
};
const GOAL_LABEL: Record<string, string> = {
  leads: 'Get enquiries', bookings: 'Get bookings', calls: 'Get phone calls',
  visits: 'Get people in the door', sell: 'Sell online', info: 'Just inform visitors',
};
const AESTHETICS = ['modern', 'warm', 'bold', 'minimal', 'professional', 'playful', 'elegant', 'rustic'];
const SLOTS: { id: string; label: string }[] = [
  { id: 'hero', label: 'Main banner' }, { id: 'about', label: 'About / story' }, { id: 'team', label: 'People' },
  { id: 'work', label: 'Our work' }, { id: 'product', label: 'Products' }, { id: 'any', label: 'Anywhere' },
];

const INPUT = 'w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50';
const LABEL = 'block text-sm font-medium text-slate-300 mb-1.5';
const HINT = 'text-xs text-slate-500 mt-1';

function val(e: Event): string { return (e.currentTarget as HTMLInputElement).value; }

function Choice<T extends string>({ value, options, onChange }: { value: T; options: { id: T; label: string; hint?: string }[]; onChange: (v: T) => void }) {
  return (
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((o) => (
        <button
          type="button"
          onClick={() => onChange(o.id)}
          class={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${value === o.id ? 'border-cyan-500/70 bg-cyan-500/10 text-slate-100' : 'border-slate-600/50 bg-dark-700/40 text-slate-300 hover:border-slate-500'}`}
        >
          <div class="font-medium">{o.label}</div>
          {o.hint && <div class="text-xs text-slate-500 mt-0.5">{o.hint}</div>}
        </button>
      ))}
    </div>
  );
}

function Uploader({ siteId, role, slot, accept, multiple, label, materials, onChange }: {
  siteId: string; role: Role; slot?: string; accept: string; multiple?: boolean; label: string;
  materials: Material[]; onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const mine = materials.filter((m) => m.role === role);

  async function upload(files: FileList) {
    if (busy) return;
    setBusy(true);
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) fd.append('files', files[i]);
    fd.append('role', role);
    if (slot) fd.append('slot', slot);
    try {
      const r = await api(`/sites/${siteId}/materials/upload`, { method: 'POST', body: fd });
      const res = await r.json().catch(() => ({})) as any;
      if (!r.ok) throw new Error(res.error || 'Upload failed');
      if (res.errors?.length) showToast('warning', 'Some files were skipped', res.errors.join(', '));
      onChange();
    } catch (err: any) {
      showToast('error', 'Upload failed', err?.message || 'Upload failed');
    } finally { setBusy(false); }
  }

  async function remove(file: string) {
    const r = await api(`/sites/${siteId}/materials/${encodeURIComponent(file)}`, { method: 'DELETE' });
    if (!r.ok) showToast('error', 'Error', 'Delete failed');
    onChange();
  }

  return (
    <div>
      <div
        onClick={() => { if (!busy) input.current?.click(); }}
        onDragOver={(e: DragEvent) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e: DragEvent) => { e.preventDefault(); setOver(false); if (e.dataTransfer?.files.length) upload(e.dataTransfer.files); }}
        class={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${over ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-slate-600/50 hover:border-cyan-500/50'}`}
      >
        <input
          ref={input} type="file" accept={accept} multiple={!!multiple} class="hidden"
          onChange={(e: Event) => { const t = e.currentTarget as HTMLInputElement; if (t.files?.length) upload(t.files); t.value = ''; }}
        />
        {busy ? <Spinner /> : <p class="text-xs text-slate-400">{label}</p>}
      </div>
      {mine.length > 0 && (
        <div class="mt-2 space-y-1">
          {mine.map((m) => (
            <div class="flex items-center justify-between bg-dark-900 rounded px-2 py-1.5">
              <span class="text-xs text-slate-300 truncate" title={m.originalName}>
                {m.file}{m.slot && m.slot !== 'any' ? <span class="text-slate-500"> · {SLOTS.find((s) => s.id === m.slot)?.label}</span> : null}
              </span>
              <button type="button" onClick={() => remove(m.file)} class="text-xs text-slate-500 hover:text-red-400 ml-2">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Interview({ site, siteId, onBuild, onSkip }: {
  site: Site | null; siteId: string; onBuild: () => void; onSkip: () => void;
}) {
  const [brief, setBrief] = useState<Brief | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [detailNudge, setDetailNudge] = useState(false);
  const [photoSlot, setPhotoSlot] = useState('hero');
  const [customPage, setCustomPage] = useState('');
  const touched = useRef<{ pages: boolean; goal: boolean }>({ pages: false, goal: false });
  const latest = useRef<Brief | null>(null);
  latest.current = brief;

  async function loadMaterials() {
    const r = await api(`/sites/${siteId}/brief`);
    if (!r.ok) return;
    const d = await r.json() as any;
    setMaterials(d.materials || []);
    return d;
  }

  useEffect(() => {
    (async () => {
      const d = await loadMaterials();
      if (d?.brief) {
        setBrief(d.brief);
        if (d.saved && d.brief.pages?.length) touched.current = { pages: true, goal: !!d.brief.goal };
      }
    })();
  }, [siteId]);

  if (!brief) {
    return <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-10 flex justify-center"><Spinner /></div>;
  }

  const b = brief;
  function set(fn: (d: Brief) => void) {
    const next = JSON.parse(JSON.stringify(b)) as Brief;
    fn(next);
    setBrief(next);
  }

  function addPage() {
    const name = customPage.trim().replace(/\s+/g, ' ');
    if (!name) return;
    const known = Object.keys(PAGE_LABEL).find((k) => k === name.toLowerCase() || PAGE_LABEL[k].toLowerCase() === name.toLowerCase());
    const page = known || name;
    touched.current.pages = true;
    set((d) => { if (!d.pages.some((x) => x.toLowerCase() === page.toLowerCase()) && d.pages.length < 12) d.pages.push(page); });
    setCustomPage('');
  }

  async function save(current: Brief = b): Promise<boolean> {
    setSaving(true);
    try {
      const r = await api(`/sites/${siteId}/brief`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ brief: current }),
      });
      if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as any).error || 'Save failed');
      return true;
    } catch (err: any) {
      showToast('error', 'Could not save', err?.message || 'Save failed');
      return false;
    } finally { setSaving(false); }
  }

  async function suggest(): Promise<Suggestion | null> {
    setThinking(true);
    try {
      const r = await api(`/sites/${siteId}/brief/suggest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ brief: b }),
      });
      if (!r.ok) return null;
      const s = await r.json() as Suggestion;
      setSuggestion(s);
      const next = JSON.parse(JSON.stringify(latest.current || b)) as Brief;
      if (!touched.current.pages) next.pages = s.pages;
      if (!touched.current.goal) next.goal = s.goal;
      setBrief(next);
      save(next);
      return s;
    } catch { return null; } finally { setThinking(false); }
  }

  const canBuild = !!b.business.name && (b.business.summary.length >= 10 || !!b.source.url);

  async function go(to: number) {
    if (step === 0 && to > 0) {
      if (!canBuild) { showToast('warning', 'A little more first', 'Add your business name and a sentence about what you do, or an existing website.'); return; }
      if (detailNudge) save();
      else {
        const s = await suggest();
        if (s?.needsDetail && !b.business.services && !b.business.audience) { setDetailNudge(true); return; }
      }
    } else {
      save();
    }
    setDetailNudge(false);
    setStep(Math.max(0, Math.min(STEPS.length - 1, to)));
  }

  async function build() {
    if (!(await save())) return;
    onBuild();
  }

  const logo = materials.find((m) => m.role === 'logo');
  const photos = materials.filter((m) => m.role === 'image');
  const copyDocs = materials.filter((m) => m.role === 'copy');
  const showLocation = suggestion?.hasLocation || !!(b.business.address || b.business.hours);

  return (
    <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
      <div class="flex items-center justify-between mb-1">
        <h2 class="text-lg font-semibold text-slate-100">{STEPS[step]}</h2>
        <span class="text-xs text-slate-500">Step {step + 1} of {STEPS.length}</span>
      </div>
      <div class="h-1 bg-dark-700 rounded-full overflow-hidden mb-5">
        <div class="h-full bg-cyan-500 transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}></div>
      </div>

      <div class="space-y-5 min-h-[300px]">
        {step === 0 && (
          <>
            <p class="text-sm text-slate-400">A few questions and we will build your first version. Skip anything you do not know.</p>
            <div>
              <label class={LABEL}>Business name *</label>
              <input class={INPUT} value={b.business.name} maxLength={100} onInput={(e: Event) => set((d) => { d.business.name = val(e); })} />
            </div>
            <div>
              <label class={LABEL}>What does the business do? *</label>
              <textarea class={INPUT} rows={3} maxLength={1500} value={b.business.summary}
                placeholder="We are a family-run bakery in Portland making sourdough and custom cakes."
                onInput={(e: Event) => set((d) => { d.business.summary = val(e); })} />
            </div>
            {detailNudge && (
              <div class="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-300">
                A bit more detail will make a much better first draft: what you offer and who it is for. Or continue anyway.
              </div>
            )}
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class={LABEL}>Main services or products</label>
                <textarea class={INPUT} rows={2} maxLength={1000} value={b.business.services} placeholder="Sourdough, wedding cakes, catering"
                  onInput={(e: Event) => set((d) => { d.business.services = val(e); })} />
              </div>
              <div>
                <label class={LABEL}>Who are your customers?</label>
                <textarea class={INPUT} rows={2} maxLength={300} value={b.business.audience} placeholder="Local families, event planners"
                  onInput={(e: Event) => set((d) => { d.business.audience = val(e); })} />
              </div>
              <div>
                <label class={LABEL}>Where are you?</label>
                <input class={INPUT} value={b.business.location} maxLength={200} placeholder="City, region, or service area"
                  onInput={(e: Event) => set((d) => { d.business.location = val(e); })} />
              </div>
              <div>
                <label class={LABEL}>Phone</label>
                <input class={INPUT} value={b.business.phone} maxLength={40} onInput={(e: Event) => set((d) => { d.business.phone = val(e); })} />
              </div>
              {showLocation && (
                <>
                  <div>
                    <label class={LABEL}>Street address</label>
                    <input class={INPUT} value={b.business.address} maxLength={300} onInput={(e: Event) => set((d) => { d.business.address = val(e); })} />
                  </div>
                  <div>
                    <label class={LABEL}>Opening hours</label>
                    <input class={INPUT} value={b.business.hours} maxLength={300} placeholder="Tue-Sat 7am-3pm" onInput={(e: Event) => set((d) => { d.business.hours = val(e); })} />
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div>
              <label class={LABEL}>Do you have a website already?</label>
              <input class={INPUT} value={b.source.url} placeholder="https://www.example.com (leave blank if not)" maxLength={300}
                onInput={(e: Event) => set((d) => { d.source.url = val(e); if (d.source.url && d.source.use === 'none') d.source.use = 'both'; })} />
            </div>
            {b.source.url && (
              <div>
                <label class={LABEL}>What should we take from it?</label>
                <Choice value={b.source.use} onChange={(v) => set((d) => { d.source.use = v; })} options={[
                  { id: 'both', label: 'Words and look', hint: 'Keep the content and the branding, build it better' },
                  { id: 'content', label: 'Just the words', hint: 'Fresh design, same information' },
                  { id: 'design', label: 'Just the look', hint: 'Same colours and logo, new content' },
                  { id: 'none', label: 'Nothing, start fresh' },
                ]} />
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <label class={LABEL}>Do you have a logo?</label>
              <Choice value={logo ? 'upload' : b.brand.logo} onChange={(v) => set((d) => { d.brand.logo = v; })} options={[
                { id: 'upload', label: 'Yes, I will upload it' },
                { id: 'wordmark', label: 'No, use my business name', hint: 'We style the name as a text logo' },
              ]} />
              {(b.brand.logo === 'upload' || logo) && (
                <div class="mt-3">
                  <Uploader siteId={siteId} role="logo" accept="image/*" label={logo ? 'Drop a new logo to replace it' : 'Drop your logo here (SVG or PNG works best)'} materials={materials} onChange={loadMaterials} />
                </div>
              )}
            </div>
            <div>
              <label class={LABEL}>Brand guidelines?</label>
              <Choice value={b.brand.guide} onChange={(v) => set((d) => { d.brand.guide = v; })} options={[
                { id: 'upload', label: 'I have a brand guide' },
                { id: 'colors', label: 'I just know my colours' },
                { id: 'none', label: 'No, pick for me' },
              ]} />
              {b.brand.guide === 'upload' && (
                <div class="mt-3">
                  <Uploader siteId={siteId} role="brand_guide" accept=".pdf,image/*,.txt" label="Drop your brand guide (PDF, image, or text)" materials={materials} onChange={loadMaterials} />
                </div>
              )}
            </div>
            {b.brand.guide !== 'none' && (
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class={LABEL}>Brand colours</label>
                  <div class="flex items-center gap-2 flex-wrap">
                    {b.brand.colors.map((c, i) => (
                      <span class="flex items-center gap-1">
                        <input type="color" value={c} class="w-9 h-9 rounded bg-transparent border border-slate-600/50"
                          onInput={(e: Event) => set((d) => { d.brand.colors[i] = val(e); })} />
                        <button type="button" class="text-xs text-slate-500 hover:text-red-400" onClick={() => set((d) => { d.brand.colors.splice(i, 1); })}>x</button>
                      </span>
                    ))}
                    {b.brand.colors.length < 4 && (
                      <button type="button" class="px-2 py-1.5 text-xs rounded border border-slate-600/50 text-slate-300 hover:border-cyan-500/50"
                        onClick={() => set((d) => { d.brand.colors.push('#0ea5e9'); })}>+ Add colour</button>
                    )}
                  </div>
                </div>
                <div>
                  <label class={LABEL}>Brand fonts</label>
                  <input class={INPUT} value={b.brand.fonts} maxLength={200} placeholder="e.g. Playfair Display, Inter"
                    onInput={(e: Event) => set((d) => { d.brand.fonts = val(e); })} />
                </div>
              </div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <div>
              <label class={LABEL}>Photos for the site</label>
              <Choice value={b.images.mode} onChange={(v) => set((d) => { d.images.mode = v; })} options={[
                { id: 'upload', label: 'I have my own photos', hint: 'Use only mine' },
                { id: 'mix', label: 'Some of mine, fill the gaps', hint: 'We generate the rest' },
                { id: 'generate', label: 'Create them for me' },
                { id: 'none', label: 'No photos', hint: 'Text and colour only' },
              ]} />
            </div>
            {(b.images.mode === 'upload' || b.images.mode === 'mix') && (
              <div>
                <label class={LABEL}>Where should these go?</label>
                <div class="flex flex-wrap gap-2 mb-3">
                  {SLOTS.map((s) => (
                    <button type="button" onClick={() => setPhotoSlot(s.id)}
                      class={`px-3 py-1.5 rounded-full text-xs border ${photoSlot === s.id ? 'border-cyan-500/70 bg-cyan-500/10 text-slate-100' : 'border-slate-600/50 text-slate-400'}`}>{s.label}</button>
                  ))}
                </div>
                <Uploader siteId={siteId} role="image" slot={photoSlot} accept="image/*" multiple
                  label={`Drop photos for: ${SLOTS.find((s) => s.id === photoSlot)?.label}`} materials={materials} onChange={loadMaterials} />
                <p class={HINT}>Max 10MB per file, 50MB total.</p>
              </div>
            )}
          </>
        )}

        {step === 4 && (
          <>
            <div>
              <label class={LABEL}>Do you have text for the site?</label>
              <Choice value={b.copy.mode} onChange={(v) => set((d) => { d.copy.mode = v; })} options={[
                { id: 'upload', label: 'Yes, upload documents' },
                { id: 'paste', label: 'Yes, I will paste it' },
                { id: 'write', label: 'No, write it for me' },
              ]} />
            </div>
            {b.copy.mode === 'upload' && (
              <Uploader siteId={siteId} role="copy" accept=".pdf,.txt,.csv" multiple label="Drop documents (PDF or text)" materials={materials} onChange={loadMaterials} />
            )}
            {b.copy.mode === 'paste' && (
              <div>
                <textarea class={INPUT} rows={10} maxLength={20000} value={b.copy.text} placeholder="Paste your About us, services, FAQs..."
                  onInput={(e: Event) => set((d) => { d.copy.text = val(e); })} />
                <p class={HINT}>We keep your wording and arrange it into sections.</p>
              </div>
            )}
          </>
        )}

        {step === 5 && (
          <>
            {thinking && <div class="flex items-center gap-2 text-xs text-slate-400"><Spinner /> Suggesting pages for your business</div>}
            <div>
              <label class={LABEL}>
                Pages {suggestion && !touched.current.pages && <span class="ml-2 text-xs font-normal text-cyan-400">Suggested for your business</span>}
              </label>
              <div class="flex flex-wrap gap-2">
                {[...Object.keys(PAGE_LABEL), ...b.pages.filter((p) => !PAGE_LABEL[p])].map((p) => {
                  const on = b.pages.includes(p);
                  return (
                    <button type="button"
                      onClick={() => { touched.current.pages = true; set((d) => { d.pages = on ? d.pages.filter((x) => x !== p) : [...d.pages, p]; }); }}
                      class={`px-3 py-1.5 rounded-full text-xs border ${on ? 'border-cyan-500/70 bg-cyan-500/10 text-slate-100' : 'border-slate-600/50 text-slate-400'}`}>{PAGE_LABEL[p] || p}</button>
                  );
                })}
              </div>
              <div class="flex gap-2 mt-3">
                <input class={INPUT} maxLength={40} value={customPage} placeholder="Add your own, e.g. Wedding packages, Our farm, Careers"
                  onInput={(e: Event) => setCustomPage(val(e))}
                  onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); addPage(); } }} />
                <button type="button" onClick={addPage} disabled={!customPage.trim()}
                  class="px-3 py-2 rounded-lg text-sm border border-slate-600/50 text-slate-300 disabled:opacity-40">Add</button>
              </div>
              <p class={HINT}>{b.pages.length <= 1 ? 'One page with sections.' : `${b.pages.length} pages.`} These are ideas, not a fixed list. Add any page you need, and you can add more after the build.</p>
            </div>
            <div>
              <label class={LABEL}>
                What should the site do for you? {suggestion && !touched.current.goal && <span class="ml-2 text-xs font-normal text-cyan-400">Suggested</span>}
              </label>
              <Choice value={b.goal} onChange={(v) => { touched.current.goal = true; set((d) => { d.goal = v; }); }}
                options={Object.entries(GOAL_LABEL).map(([id, label]) => ({ id, label }))} />
            </div>
            <div>
              <label class={LABEL}>Main button text</label>
              <input class={INPUT} value={b.cta} maxLength={120} placeholder="e.g. Book a table" onInput={(e: Event) => set((d) => { d.cta = val(e); })} />
            </div>
          </>
        )}

        {step === 6 && (
          <>
            <div>
              <label class={LABEL}>How should it feel? (up to 3)</label>
              <div class="flex flex-wrap gap-2">
                {AESTHETICS.map((a) => {
                  const on = b.style.aesthetic.includes(a);
                  return (
                    <button type="button"
                      onClick={() => set((d) => { d.style.aesthetic = on ? d.style.aesthetic.filter((x) => x !== a) : [...d.style.aesthetic, a].slice(-3); })}
                      class={`px-3 py-1.5 rounded-full text-xs border capitalize ${on ? 'border-cyan-500/70 bg-cyan-500/10 text-slate-100' : 'border-slate-600/50 text-slate-400'}`}>{a}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <label class={LABEL}>Websites you like</label>
              <input class={INPUT} value={b.style.references} maxLength={500} placeholder="Links or names, and what you like about them"
                onInput={(e: Event) => set((d) => { d.style.references = val(e); })} />
            </div>
            <div>
              <label class={LABEL}>Anything to avoid?</label>
              <input class={INPUT} value={b.style.avoid} maxLength={500} placeholder="e.g. no stock photos of handshakes"
                onInput={(e: Event) => set((d) => { d.style.avoid = val(e); })} />
            </div>
            <div>
              <label class={LABEL}>Anything else?</label>
              <textarea class={INPUT} rows={3} maxLength={1500} value={b.notes} onInput={(e: Event) => set((d) => { d.notes = val(e); })} />
            </div>
          </>
        )}

        {step === 7 && (
          <div class="space-y-2 text-sm">
            {[
              ['Business', `${b.business.name}${b.business.location ? `, ${b.business.location}` : ''}`, 0],
              ['About', b.business.summary || '-', 0],
              ['Existing site', b.source.url ? `${b.source.url} (${b.source.use === 'none' ? 'start fresh' : b.source.use === 'both' ? 'words and look' : b.source.use === 'content' ? 'words' : 'look'})` : 'None', 1],
              ['Logo', logo ? logo.file : 'Text wordmark', 2],
              ['Brand', [b.brand.colors.join(' '), b.brand.fonts, materials.some((m) => m.role === 'brand_guide') ? 'brand guide uploaded' : ''].filter(Boolean).join(', ') || 'We pick', 2],
              ['Photos', `${{ upload: 'Mine only', mix: 'Mine plus generated', generate: 'Generated', none: 'None' }[b.images.mode]}${photos.length ? ` (${photos.length} uploaded)` : ''}`, 3],
              ['Words', b.copy.mode === 'write' ? 'Written for you' : b.copy.mode === 'paste' ? `Pasted (${b.copy.text.length} chars)` : `${copyDocs.length} document(s)`, 4],
              ['Pages', b.pages.length ? b.pages.map((p) => PAGE_LABEL[p] || p).join(', ') : 'We decide', 5],
              ['Goal', `${GOAL_LABEL[b.goal] || 'We decide'}${b.cta ? `, "${b.cta}"` : ''}`, 5],
              ['Feel', b.style.aesthetic.join(', ') || 'We decide', 6],
            ].map(([k, v, s]) => (
              <div class="flex gap-3 bg-dark-900 rounded-lg px-3 py-2">
                <span class="w-28 flex-shrink-0 text-slate-500">{k}</span>
                <span class="flex-1 text-slate-200 break-words">{v}</span>
                <button type="button" class="text-xs text-cyan-400 hover:text-cyan-300" onClick={() => go(s as number)}>Edit</button>
              </div>
            ))}
            {b.images.mode === 'upload' && photos.length === 0 && (
              <div class="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-300">You chose your own photos but have not uploaded any, so the site will have none.</div>
            )}
            {!site?.email && (
              <div class="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-300">
                No contact email set for this site. <a href={`/sites/${siteId}/settings`} class="underline">Add one in Settings</a> before building.
              </div>
            )}
          </div>
        )}
      </div>

      <div class="flex items-center justify-between pt-4 mt-5 border-t border-slate-700/50">
        <div class="flex items-center gap-3">
          {step > 0 && <button type="button" onClick={() => go(step - 1)} class="px-3 py-2 text-sm text-slate-300 hover:bg-dark-700 rounded-lg">Back</button>}
          {step === 0 && <button type="button" onClick={onSkip} class="px-4 py-2 text-sm font-medium text-slate-200 border border-slate-600 hover:border-slate-500 hover:bg-dark-700 rounded-lg transition-colors">Skip the questions, just describe it</button>}
          {saving && <span class="text-xs text-slate-500">Saving</span>}
        </div>
        <div class="flex items-center gap-2">
          {step > 0 && step < STEPS.length - 1 && canBuild && (
            <button type="button" onClick={() => go(STEPS.length - 1)} class="px-3 py-2 text-sm text-slate-400 hover:text-slate-200">Skip to review</button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" disabled={thinking} onClick={() => go(step + 1)} class="px-5 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg disabled:opacity-60">
              {thinking ? <ButtonSpinner /> : detailNudge ? 'Continue anyway' : 'Next'}
            </button>
          ) : (
            <button type="button" disabled={saving || !canBuild || !site?.email} onClick={build} class="px-6 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg disabled:opacity-60">
              Build my site
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
